import React from 'react';
import { motion } from 'framer-motion';
import { TextField } from './fields/TextField';
import { TextAreaField } from './fields/TextAreaField';
import { SelectField } from './fields/SelectField';
import { RadioField } from './fields/RadioField';
import { CheckboxField } from './fields/CheckboxField';
import { ScaleField } from './fields/ScaleField';

export const AssessmentSection = ({ section, formData, updateField, errors }) => {
  const sectionData = formData[section.id] || {};

  const renderField = (field) => {
    const fieldValue = sectionData[field.id];
    const fieldError = errors[`${section.id}.${field.id}`];
    
    // Check conditional rendering
    if (field.conditional_on) {
      const conditionField = sectionData[field.conditional_on.field];
      if (field.conditional_on.contains) {
        // For multi-select: check if array contains the value
        if (Array.isArray(conditionField)) {
          if (!conditionField.includes(field.conditional_on.contains)) {
            return null; // Don't render this field
          }
        } else if (conditionField !== field.conditional_on.contains) {
          return null; // Don't render this field
        }
      }
    }
    
    const commonProps = {
      id: field.id,
      label: field.label,
      value: fieldValue !== undefined && fieldValue !== null ? fieldValue : (field.type === 'checkbox_multiple' ? [] : ''),
      onChange: (value) => updateField(section.id, field.id, value),
      required: field.required,
      error: fieldError,
      helpText: field.help_text || field.placeholder,
    };

    switch (field.type) {
      case 'text':
      case 'email':
        return (
          <TextField
            key={field.id}
            {...commonProps}
            type={field.type}
            placeholder={field.placeholder}
          />
        );

      case 'textarea':
        return (
          <TextAreaField
            key={field.id}
            {...commonProps}
            placeholder={field.placeholder}
            rows={4}
          />
        );

      case 'select':
        return (
          <SelectField
            key={field.id}
            {...commonProps}
            options={field.options}
          />
        );

      case 'radio':
        return (
          <RadioField
            key={field.id}
            {...commonProps}
            options={field.options}
          />
        );

      case 'checkbox_multiple':
        return (
          <CheckboxField
            key={field.id}
            {...commonProps}
            options={field.options}
            allowOther={field.allow_other}
          />
        );

      case 'scale':
        return (
          <ScaleField
            key={field.id}
            {...commonProps}
            scaleMin={field.scale_min}
            scaleMax={field.scale_max}
            scaleLabels={field.scale_labels}
          />
        );

      case 'date':
        return (
          <TextField
            key={field.id}
            {...commonProps}
            type="date"
          />
        );

      default:
        return (
          <TextField
            key={field.id}
            {...commonProps}
          />
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      {/* Section Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-3">{section.title}</h2>
        {section.description && (
          <p className="text-lg text-gray-400">{section.description}</p>
        )}
      </div>

      {/* Fields */}
      <div className="space-y-6">
        {section.fields.map(field => renderField(field))}
      </div>
    </motion.div>
  );
};

